import { Paper, CircularProgress, Button, Tooltip } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { format } from 'date-fns';
import { isNull, zip } from 'lodash';
import { object } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { DynamicForm } from '../../components/DynamicForm';
import states from '../../helpers/constants/states';
import * as v from '../../helpers/validation';
import InfoTooltip from '../InfoTooltip';
// SERVICES
import {
  createFileImportOrder,
  getAvailableReportCodes,
  getClientList,
  getFileImportRushTypes,
  getFileImportProntoStructureTypes,
  getZipCodeDetails,
  getAgentOptions,
  getAgentById,
  getUnderwriterOptions,
  getProductOptions,
  getClientBundles,
  getClientBundleOptions,
} from '../../services';
import { Autocomplete } from '../Autocomplete';
import ColoredCheckbox from '../ColoredInputs/ColoredCheckbox';
import { FormControlLabel } from '@material-ui/core';
import AsyncSelect from '../DynamicForm/FormInputs/Select/AsyncSelect';
import AgentCreateForm from './AgentCreateForm';

const customFuzzySearch = (option, rawInput) => {
  const words = rawInput.split(' ');
  return words.reduce((acc, cur) => acc && option.label.toLowerCase().includes(cur.toLowerCase()), true);
};

const styles = (props) => ({ paper: { padding: '24px', boxSizing: 'border-box' } });

export const FileImportForm = withStyles(styles)(({ classes, isAdmin = false }) => {
  const [showAgentCreationDialog, setShowAgentCreationDialog] = useState(false);
  const [client, setClient] = useState({
    loading: false,
    result: null,
  });
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientOptions, setClientOptions] = useState({
    results: [],
    loading: true,
    loaded: false,
  });
  const [multiLocationChecked, setMultiLocationChecked] = useState();
  const [reportCodes, setReportCodes] = useState(null);
  const [rushTypes, setRushTypes] = useState(null);
  const [prontoStructureTypes, setProntoStructureTypes] = useState(null);
  const [zipCodeDetails, setZipCodeDetails] = useState(null);
  const [agentDetails, setAgentDetails] = useState(null);
  const [underwriterDetails, setUnderwriterDetails] = useState(null);
  const [reportDetails, setReportDetails] = useState(null);
  const [agentSelectKey, setAgentSelectKey] = useState(null);
  const [agentEmails, setAgentEmails] = useState(null);
  const [isValidZipCode, setValidZipCode] = useState(true);
  const [useOriginalAddress, setUseOriginalAddress] = useState(false);
  const [isVirtual, setIsVirtual] = useState(false);

  const getClientListAsync = async () => {
    try {
      const results = await getClientList();

      if (isAdmin) {
        setClientOptions({
          loading: false,
          loaded: true,
          results: results.map((c) => ({
            label: `${c.name} (${c.code})`,
            value: c.id,
          })),
        });
      } else {
        setClient({
          loading: false,
          result: results[0],
        });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const resetDetails = () => {
    setReportCodes(null);
    setZipCodeDetails(null);
    setAgentDetails(null);
    setUnderwriterDetails(null);
    setReportDetails(null);
  };

  const getClientFromSelectedOption = async () => {
    try {
      resetDetails();
      setClient({
        result: null,
        loading: true,
      });

      const results = await getClientList();

      const clientObj = results.find((c) => c.id === selectedClient.value);

      setClient({
        result: clientObj,
        loading: false,
      });
    } catch (error) {
      console.log(error);
      setClient({
        ...client,
        loading: false,
      });
    }
  };

  const getRushTypes = async () => {
    let fileImportRushTypes = await getFileImportRushTypes();
    fileImportRushTypes = fileImportRushTypes.filter((x) => x.serviceTypeId === 1015);
    fileImportRushTypes = fileImportRushTypes
      .map(({ name, orderable, receivableAmount }) => {
        return { value: name, label: `${name} ($${receivableAmount} surcharge)`, orderable };
      })
      .filter(({ orderable }) => orderable);
    setRushTypes(fileImportRushTypes);
  };

  const getProntoStructureTypes = async () => {
    let fileImportProntoStructureTypes = await getFileImportProntoStructureTypes();

    fileImportProntoStructureTypes = fileImportProntoStructureTypes.map(({ value }) => {
      return { value: value, label: value };
    });

    setProntoStructureTypes(fileImportProntoStructureTypes);
  };

  const handleInspectionTypeSelection = (selectedInspectionType) => {
    // get report codes related to this inspection type
    setReportDetails({
      inspectionType: selectedInspectionType,
    });

    getAvailableReportCodesAsync(selectedInspectionType);
  };

  const fetchZipCodeDetails = async (zipCode) => {
    if (!zipCode || zipCode.length != 5) {
      setZipCodeDetails(null);
      setValidZipCode(true);
      return;
    }
    try {
      const result = await getZipCodeDetails(zipCode);
      setZipCodeDetails(result);
      setValidZipCode(true);
    } catch (error) {
      setValidZipCode(false);
      console.log(error);
    }
  };

  const test = async (ev) => {
    console.log(ev);
  };

  const updateNullEmails = (emails) => {
    if (!emails) {
      setAgentEmails(agentDetails?.email);
      return;
    }
    setAgentEmails(emails);
  };

  /**
   * @func onSave
   * @desc handle the saving of the form
   * - in this case, add the hackaround fields back to the form data (since I place them in the placeholder and not actually in the object)
   * @param {*} formData
   */
  const onSave = (formData) => {
    // console.log(agentEmails);
    // console.log(formData.agentcontactemail);
    // console.log(formData.rushType);
    const updatedFormData = {
      ...formData,

      // address inputs
      insuredcity: zipCodeDetails?.city.label ?? formData?.insuredcity,
      insuredstate: zipCodeDetails?.state.label ?? formData?.insuredstate,

      // agent inputs
      agentName: agentDetails?.agentName,
      agentCode: agentDetails?.agentCode,
      agentPhone: agentDetails?.agentPhone,
      agentEmail: agentDetails?.agentEmail,
      //sub agent inputs
      agentcontactname: isNull(formData.agentcontactname) ? null : formData.agentcontactname,
      agentcontactemail:
        formData.agentcontactemail == undefined || formData.agentcontactemail == null
          ? agentEmails
          : formData.agentcontactemail,
    };
    setIsVirtual(false);
    // console.log(updatedFormData);
    return createFileImportOrder(updatedFormData, client.result?.id, useOriginalAddress);
  };

  const getAvailableReportCodesAsync = async (inspectionType) => {
    const currentInspectionType = inspectionType || reportDetails?.inspectionType || null;

    let products = await getClientBundlesAsync(currentInspectionType);

    const sortedProducts = products.sort((a, b) => (a.label < b.label ? -1 : 1));

    setReportCodes(sortedProducts); //sortedProducts);
  };

  // get client bundles
  const getClientBundlesAsync = async (inspectionType) => {
    try {
      const results = await getClientBundleOptions({
        clientId: client.result?.id,
        inspectionType: inspectionType || reportDetails?.inspectionType || null,
      });

      return results;
    } catch (error) {
      console.log(error);
      return [];
    }
  };

  const getSelectedAgentInfo = async (selectedAgentCode) => {
    if (!selectedAgentCode) {
      setAgentDetails(null);
      return;
    }

    try {
      const agent = await getAgentById(selectedAgentCode);
      setAgentDetails(agent);
    } catch (error) {
      console.log(error);
    }
  };

  const onSaveSuccess = () => {
    if (!multiLocationChecked) {
      // reset the state for the next order
      setShowAgentCreationDialog(false);
      setClient({
        loading: false,
        result: null,
      });
      setSelectedClient(null);
      setReportCodes(null);
      setZipCodeDetails(null);
      setAgentDetails(null);
      setUnderwriterDetails(null);
      setReportDetails(null);

      // restart the page logic
      onLoad();
    } else {
      // TODO: reset address values
      setZipCodeDetails(null);
    }
  };

  const onLoad = () => {
    getClientListAsync();
    getRushTypes();
    getProntoStructureTypes();
  };

  useEffect(() => {
    if (selectedClient) {
      getClientFromSelectedOption();
    }
  }, [selectedClient]);

  useEffect(() => {
    // fetch agents again in case an agent was created
    setAgentSelectKey(Math.random());
  }, [showAgentCreationDialog]);

  // fetch available report codes whenever the client changes
  useEffect(() => {
    if (client.result) {
      getAvailableReportCodesAsync();
    }
  }, [client.result]);

  useEffect(() => {
    onLoad();
  }, []);

  const fileImportOrderInputs = [
    {
      inputType: 'gridcontainer',
      children: [
        {
          inputType: 'griditem',
          children: [
            {
              inputType: 'select',
              field: 'inspectiontype',
              label: 'Inspection Type',
              validate: [],
              options: [
                { label: 'Personal Lines', value: 'P' },
                { label: 'Commercial Lines', value: 'C' },
                // { label: 'Project', value: '' },
                // { label: 'High Value', value: 'HV' },
              ],
              onChange: handleInspectionTypeSelection,
            },
            {
              inputType: 'select',
              field: 'reportcode',
              label: 'Report Code',
              validate: [v.required()],
              options: reportCodes,
              filterOption: customFuzzySearch,
            },
            {
              inputType: 'text',
              field: 'orderDate',
              label: 'Order Date',
              validate: [v.required()],
              disabled: true,
            },
            {
              inputType: 'text',
              field: 'customerCode',
              label: 'Customer Code',
              validate: [v.required()],
              disabled: true,
            },

            {
              inputType: 'text',
              field: 'policy',
              label: 'Policy Number',
              validate: [v.required(), v.maxLength(100)],
            },
            {
              inputType: 'date',
              field: 'policyeffectivedate',
              label: 'Policy Effective Date',
              validate: [],
            },
            {
              inputType: 'date',
              field: 'policyexpirationdate',
              label: 'Policy Expiration Date',
              validate: [],
            },
            {
              inputType: 'text',
              field: 'insuredname',
              // label: 'Contact Name', // used to be Insured Name
              label: 'Insured Name',
              validate: [v.required(), v.maxLength(150)],
            },
            {
              inputType: 'text',
              field: 'dba',
              label: 'DBA',
              validate: [],
            },
            {
              inputType: 'text',
              field: 'insuredaddress',
              label: 'Insured Address',
              validate: [v.required(), v.maxLength(100)],
            },
            {
              inputType: 'text',
              field: 'insuredzip',
              label: 'Insured Zip',
              validate: [v.required(), v.hasLength(5, 'Zip codes require 5 numbers')],
              isClearable: true,
              onChange: fetchZipCodeDetails,
            },
            {
              inputType: 'text',
              field: 'insuredcity',
              label: 'Insured City',
              placeholder: zipCodeDetails?.city?.label,
              disabled: isValidZipCode,
              validate: [zipCodeDetails?.city?.label == undefined ? v.requireWithPlaceholder() : '', v.maxLength(100)],
            },
            {
              inputType: 'text',
              field: 'insuredstate',
              label: 'Insured State',
              placeholder: zipCodeDetails?.state?.label,
              disabled: isValidZipCode,
              validate: [
                zipCodeDetails?.state?.label == undefined ? v.requireWithPlaceholder() : '',
                v.maxLength(2),
                v.alpha(),
              ],
            },
            {
              inputType: 'phone',
              field: 'insuredphone1',
              label: 'Insured/Contact Phone #1',
              validate: [!isVirtual ? v.required() : '', v.maxLength(12), v.phone()],
            },
            {
              inputType: 'phone',
              field: 'insuredphone2',
              label: 'Insured/Contact Phone #2',
              validate: [v.maxLength(12), v.phone()],
            },
            {
              inputType: 'text',
              field: 'insuredEmail',
              label: 'Insured/Contact Email',
              validate: [],
            },

            // NEEDS ADDED TO IMPORT LINE BEFORE IT CAN BE USED
            // {
            //   inputType: 'text',
            //   field: 'insuredemail',
            //   label: 'Insured/Contact Email',
            //   validate: [],
            // },
          ],
        },
        {
          inputType: 'griditem',
          children: [
            {
              inputType: 'custom',
              field: 'agentInfo_FIELD_NOT_IN_USE', // this select is used to populate the following agent inputs
              label: 'Search for an agency by name or code',
              validate: [v.required()],
              customComponent: (
                <AsyncSelect
                  key={agentSelectKey}
                  isMutli
                  isClearable
                  defaultValue={agentDetails?.agentId}
                  fetchOptions={(opts) =>
                    getAgentOptions({
                      ...opts,
                      clientId: client.result?.id,
                    })
                  }
                  queryParamDefaultKey={'agentId'}
                />
              ),
              // customComponent: <AsyncSelect isMutli defaultInspectorId={detailData?.inspectorId} />,
              onChange: getSelectedAgentInfo,
            },
            {
              inputType: 'text',
              field: 'agentname',
              label: 'Agency Name',
              placeholder: agentDetails?.agentName,
              disabled: true,
              validate: [v.maxLength(150)],
            },
            {
              inputType: 'text',
              field: 'agentcode',
              label: 'Agency Code',
              placeholder: agentDetails?.agentCode,
              disabled: true,
            },
            {
              inputType: 'text',
              field: 'agentphone',
              label: 'Agency Phone',
              placeholder: agentDetails?.agentPhone,
              disabled: true,
            },

            // NEEDS ADDED TO IMPORT LINE BEFORE IT CAN BE USED
            {
              inputType: 'text',
              field: 'agentemail',
              label: 'Agency Email',
              placeholder: agentDetails?.agentEmail,
              value: agentEmails,
              disabled: true,
              //validate: [v.required(), v.email()],
              //onChange: updateNullEmails,
            },
            {
              inputType: 'text',
              field: 'agentcontactname',
              label: 'Agent Contact',
              disabled: false,
            },
            {
              inputType: 'text',
              field: 'agentcontactemail',
              label: 'Agent Contact Email',
              placeholder: agentEmails,
              disabled: false,
              validate: [v.email()],
            },
            {
              inputType: 'custom',
              field: 'underwriterCode',
              label: 'Underwriter/Processor (if programmed for individual report delivery, this will be the recipient)',
              validate: [client?.result?.primaryEmailNull ? v.required() : ''],
              customComponent: (
                <AsyncSelect
                  excludeEmailOutFalse={true}
                  isMutli
                  defaultValue={underwriterDetails?.underwriterCode}
                  isPrimaryEmailNull={client?.result?.primaryEmailNull}
                  fetchOptions={(queryOpts) => {
                    return getUnderwriterOptions({
                      ...queryOpts,
                      clientId: client.result?.id,
                    });
                  }}
                  queryParamDefaultKey={'underwriterCode'}
                />
              ),
            },
            {
              inputType: 'textarea',
              field: 'specialinstructions',
              label: 'Special Instructions',
              validate: [v.maxLength(3000)],
            },
            {
              inputType: 'textarea',
              field: 'briefdescriptionofoperations',
              label: 'Brief Description of Operations',
              validate: [
                //
                v.maxLength(255),
                reportDetails?.inspectionType === 'C' ? v.required() : null,
              ].filter((id) => id),
            },
            {
              inputType: 'select',
              field: 'rushtype',
              label: 'Rush Request (if required)',
              validate: [],
              options: rushTypes,
              isClearable: true,
            },
            {
              inputType: 'text',
              field: 'yearofconstruction',
              label: 'Year of Construction',
              validate: [v.number()],
            },
            {
              inputType: 'text',
              field: 'buildingcoverage',
              label: 'Coverage A (Required for ITV Variance)',
              validate: [v.number()],
            },
            {
              inputType: 'text',
              field: 'contentscoverage',
              label: 'Coverage B',
              validate: [v.number()],
            },
            {
              inputType: 'select',
              field: 'prontoStructureType',
              label: 'Structure Type (Optional, Required for Commercial Virtual Order)',
              /*validate: [
                reportDetails?.inspectionType === 'C' ? v.required() : null,
              ],*/
              options: prontoStructureTypes,
              isClearable: true,
            },
            {
              inputType: 'text',
              field: 'customerreferenceno',
              label: 'Customer Reference Number (Optional, for internal Client Data Mining Identification)',
              validate: [v.maxLength(60)],
            },
            {
              inputType: 'text',
              field: 'programcode',
              label: 'Program Code (Optional, for internal Client Data Mining Identification)',
              validate: [v.maxLength(50)],
            },
          ],
        },
      ],
    },
  ];

  const handleMultiLocationCheckbox = (evt) => {
    let { checked } = evt.target;
    setMultiLocationChecked(checked);
  };

  return (
    <div className="flex-1 flex-column">
      <div className="row-end-center mb-20">
        {isAdmin && (
          <Autocomplete
            loading={clientOptions.loading}
            options={clientOptions.results}
            onChange={setSelectedClient}
            value={selectedClient}
            placeholder={'Select a client before creating an order or creating an agent'}
          />
        )}
        <Button
          onClick={() => setShowAgentCreationDialog(true)}
          variant="contained"
          style={{
            backgroundColor: '#315382',
            color: 'white',
            width: 200,
            marginLeft: 25,
            height: 49,
            opacity: client.result ? 1 : 0.75,
          }}
          disabled={!client.result}
        >
          Create an Agent
        </Button>
      </div>
      <div className="row-start-start">
        <InfoTooltip title="Retains information in the form except location">
          <FormControlLabel
            control={<ColoredCheckbox onChange={handleMultiLocationCheckbox} checked={multiLocationChecked} />}
            label="Multi-Location Policy"
            labelPlacement="start"
          />
        </InfoTooltip>
      </div>
      {!client.loading && !!client.result && reportCodes ? (
        <Paper className={classnames(classes.paper, 'border-left-green')} elevation={1}>
          <DynamicForm
            data={{
              orderDate: format(new Date(), 'MM/dd/yyyy'),
              customerCode: client.result.code,
            }}
            inputs={fileImportOrderInputs}
            mode="EDIT"
            onSave={onSave}
            onSaveSuccess={onSaveSuccess}
            saveButtonText="SUBMIT"
            clearStateAfterSave={false}
            multiLocation={multiLocationChecked}
            clearLocation={isValidZipCode}
            validateAddress={true}
            useOriginalAddress={useOriginalAddress}
            setUseOriginalAddress={setUseOriginalAddress}
            setIsVirtual={setIsVirtual}
          />
        </Paper>
      ) : client.loading ? (
        <div className="flex-1 col-center-center text-neutral">
          <CircularProgress style={{ fill: '#315382', color: '#315382' }} size={100} />
        </div>
      ) : (
        isAdmin && <div className="flex-1 col-center-center text-neutral">Please select a client above to continue</div>
      )}

      <AgentCreateForm
        //
        open={showAgentCreationDialog}
        setOpen={setShowAgentCreationDialog}
        client={client.result}
      />
    </div>
  );
});
